const CAP15 = {
  id: "tokens-15",
  slug: "cuestiones-de-seguridad-y-privacidad",
  contentId: 15,
  title: "Cuestiones de Seguridad y Privacidad",
  sections: [
    {
      id: "hero",
      heading: "Cuestiones de Seguridad y Privacidad en los Sistemas NFT",
      text: `La seguridad y la privacidad son pilares esenciales en cualquier sistema basado en blockchain. 
En el caso de los NFT, donde los activos digitales representan propiedad, identidad o valor financiero, 
cualquier vulnerabilidad puede tener consecuencias económicas, legales o reputacionales graves. 
Aunque la blockchain garantiza integridad e inmutabilidad, el ecosistema que la rodea —almacenamiento, interfaces, wallets y metadatos— 
aún enfrenta riesgos significativos.`
    },
    {
      id: "riesgos-de-datos",
      heading: "Riesgos en el almacenamiento y gestión de datos",
      text: `En la mayoría de los proyectos NFT, los datos asociados al token (imágenes, metadatos, audio, modelos 3D, etc.)
no se almacenan directamente en la blockchain debido al alto costo del gas y a la limitación de espacio.
En su lugar, se guarda un **hash** o una **URI** (enlace) que apunta al recurso externo.`,
      list: [
        {
          title: "Pérdida de enlace o corrupción de archivos",
          text: "Si el archivo original se aloja en un servidor centralizado y este deja de operar, el NFT pierde su valor informativo o artístico. Esto ocurre con frecuencia cuando el emisor no mantiene el hosting a largo plazo."
        },
        {
          title: "Dependencia de almacenamiento externo (IPFS o similares)",
          text: "Aunque IPFS es descentralizado, no garantiza persistencia. Si el único nodo que conserva el archivo se desconecta, el contenido deja de estar disponible. El NFT seguiría existiendo on-chain, pero sin su referencia válida."
        },
        {
          title: "Riesgos de manipulación de metadatos",
          text: "Un enlace puede apuntar a un archivo distinto al original, permitiendo suplantaciones o versiones alteradas del activo. La blockchain no puede distinguir si el contenido real fue cambiado fuera de ella."
        },
        {
          title: "Integridad no verificable del recurso externo",
          text: "Si los hashes o URIs no están protegidos criptográficamente, cualquier cambio fuera de la cadena puede pasar desapercibido para los usuarios y coleccionistas."
        }
      ]
    },
    {
      id: "mitigacion-de-riesgos",
      heading: "Estrategias de mitigación",
      text: `Para reducir los riesgos en los esquemas NFT, diversos proyectos han adoptado soluciones híbridas
que combinan almacenamiento descentralizado con verificación criptográfica.`,
      list: [
        {
          title: "Uso de IPFS con anclaje permanente",
          text: "Servicios como Filecoin o Arweave permiten garantizar que el archivo asociado al NFT se mantenga replicado en múltiples nodos durante años o décadas."
        },
        {
          title: "Verificación mediante hash en metadatos",
          text: "Incluir un hash SHA-256 del archivo dentro del JSON de metadatos permite validar que el contenido descargado coincide con la versión original registrada."
        },
        {
          title: "Integración de servicios descentralizados (NFT.Storage, Pinata, Crust)",
          text: "Proveen APIs y mecanismos de replicación automática que aseguran la permanencia del contenido asociado al token."
        },
        {
          title: "Auditoría continua de URIs",
          text: "Implementar verificadores automáticos que alerten si algún recurso vinculado al NFT deja de ser accesible o ha sido modificado."
        }
      ]
    },
    {
      id: "anonimato-y-privacidad",
      heading: "Anonimato, pseudoanonimato y trazabilidad",
      text: `Las transacciones NFT se ejecutan sobre blockchains públicas (principalmente Ethereum y compatibles con EVM),
donde las direcciones son pseudoanónimas: no revelan la identidad real del usuario,
pero todas las actividades de esa dirección son visibles y trazables.`,
      list: [
        {
          title: "Exposición de historial financiero y de propiedad",
          text: "Cualquier persona puede ver qué NFT posee una dirección, cuándo fue adquirido, cuánto pagó y a quién lo vendió. Esto crea un perfil público del comportamiento financiero del usuario."
        },
        {
          title: "Vinculación entre identidades on-chain y off-chain",
          text: "Cuando un usuario utiliza la misma dirección para múltiples actividades o la asocia a un perfil público (por ejemplo, una cuenta de Twitter), su anonimato se rompe parcialmente."
        },
        {
          title: "Limitaciones del pseudoanonimato",
          text: "Aunque el sistema no almacena nombres, las relaciones entre direcciones y transacciones pueden inferirse mediante análisis de grafos, comprometiendo la privacidad."
        }
      ]
    },
    {
      id: "tecnologias-de-privacidad",
      heading: "Tecnologías criptográficas para mejorar la privacidad",
      text: `La investigación en privacidad blockchain ha desarrollado técnicas avanzadas que podrían aplicarse a los NFT,
aunque todavía son poco comunes por su complejidad técnica y costo computacional.`,
      list: [
        {
          title: "Pruebas de conocimiento cero (Zero-Knowledge Proofs, ZKP)",
          text: "Permiten demostrar que una afirmación es verdadera sin revelar información adicional. Se usan en redes como Zcash o zkSync y podrían aplicarse para ocultar el historial de propiedad de un NFT manteniendo la validez de la transacción."
        },
        {
          title: "Encriptación homomórfica",
          text: "Facilita el procesamiento de datos cifrados sin necesidad de descifrarlos. Su adopción en NFT podría habilitar intercambios privados de metadatos sensibles (como certificados o identidad digital)."
        },
        {
          title: "Firmas en anillo (Ring Signatures)",
          text: "Permiten firmar transacciones de forma colectiva, donde no se puede identificar quién firmó realmente. Se utilizan en Monero y podrían proteger la identidad de los creadores o compradores."
        },
        {
          title: "Cálculo multipartito seguro (MPC)",
          text: "Divide la información en múltiples partes distribuidas en distintos nodos, de modo que ninguna entidad individual pueda acceder a los datos completos."
        }
      ]
    },
    {
      id: "amenazas-operativas",
      heading: "Amenazas operativas y vectores de ataque",
      text: `La seguridad no depende únicamente del protocolo, sino también de los puntos de interacción humana y del software externo al contrato.`,
      list: [
        {
          title: "Ataques a wallets",
          text: "Phishing, malware o aprobaciones maliciosas pueden permitir que un atacante transfiera todos los NFT del usuario con una sola firma."
        },
        {
          title: "Vulnerabilidades en contratos inteligentes",
          text: "Errores en la lógica de los contratos (como reentradas o falta de validación en transferencias) pueden comprometer la seguridad del sistema completo."
        },
        {
          title: "Robo de metadatos o claves privadas",
          text: "Guardar claves o URIs sensibles sin cifrar en entornos públicos (como repositorios o APIs) puede facilitar la exfiltración de datos."
        },
        {
          title: "Dependencia de terceros centralizados",
          text: "Plataformas que gestionan NFT o custodian activos digitales pueden ser vulnerables a hackeos o censura, contradiciendo el principio de descentralización."
        }
      ]
    },
    {
      id: "tendencias-futuras",
      heading: "Tendencias futuras en seguridad y privacidad de NFT",
      text: `El ecosistema blockchain avanza hacia soluciones más seguras y privadas. 
Diversos proyectos están desarrollando herramientas que combinan interoperabilidad, encriptación y descentralización total.`,
      list: [
        {
          title: "NFTs confidenciales (zkNFTs)",
          text: "Integran pruebas de conocimiento cero para ocultar información sensible (por ejemplo, identidad del propietario o metadatos privados)."
        },
        {
          title: "Gestión de identidad descentralizada (DID + VC)",
          text: "Los estándares de identidad verificable permiten controlar quién accede a la información asociada a un NFT, como títulos académicos o documentos legales."
        },
        {
          title: "Auditorías y seguridad formal",
          text: "Los contratos inteligentes comienzan a verificarse con herramientas matemáticas (formal verification) que prueban su corrección y previenen exploits."
        },
        {
          title: "Cifrado híbrido y almacenamiento soberano",
          text: "Combina almacenamiento descentralizado con cifrado cliente-servidor, permitiendo que el usuario controle completamente sus datos sin depender de un proveedor."
        },
        {
          title: "Computación confidencial",
          text: "Uso de enclaves seguros (TEE) o hardware criptográfico para procesar datos privados fuera de la cadena sin comprometer la confianza."
        }
      ]
    },
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `La seguridad y la privacidad en los sistemas NFT no se limitan al código de la blockchain, 
sino que abarcan todo el ciclo de vida del activo: creación, almacenamiento, intercambio y preservación. 
El desafío consiste en equilibrar la transparencia necesaria para la confianza pública 
con el derecho individual a la confidencialidad. 
A medida que evolucionen las técnicas criptográficas y los modelos de identidad descentralizada, 
los NFT podrán ofrecer mayor protección, soberanía y permanencia de valor digital.`
    }
  ]
};

export default CAP15;
